import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Typography,
} from '@material-ui/core'

import { DocuSealFieldDefinition } from '../../hooks/Outreach/useSignableDocs'

interface SignableDocFieldPickerDialogProps {
    open: boolean
    fieldDefinitions: DocuSealFieldDefinition[]
    fieldCategories?: Record<string, string>
    initialSelected: string[]
    onClose: () => void
    onConfirm: (selected: string[]) => void
    isSaving?: boolean
    confirmLabel?: string
}

const DEFAULT_CATEGORY_ORDER = ['applicant', 'guarantor', 'property']

export const SignableDocFieldPickerDialog: React.FC<SignableDocFieldPickerDialogProps> = ({
    open,
    fieldDefinitions,
    fieldCategories,
    initialSelected,
    onClose,
    onConfirm,
    isSaving = false,
    confirmLabel = 'Continue to builder',
}) => {
    const [selected, setSelected] = useState<string[]>(initialSelected)

    useEffect(() => {
        if (open) {
            setSelected(initialSelected)
        }
    }, [open, initialSelected])

    const selectedSet = useMemo(() => new Set(selected), [selected])

    const groupedFields = useMemo(() => {
        const groups = new Map<string, DocuSealFieldDefinition[]>()
        for (const field of fieldDefinitions) {
            const category = field.category ?? 'applicant'
            const list = groups.get(category) ?? []
            list.push(field)
            groups.set(category, list)
        }
        const ordered: Array<{
            key: string
            label: string
            fields: DocuSealFieldDefinition[]
        }> = []
        for (const key of DEFAULT_CATEGORY_ORDER) {
            const fields = groups.get(key)
            if (fields?.length) {
                ordered.push({
                    key,
                    label: fieldCategories?.[key] ?? key,
                    fields,
                })
                groups.delete(key)
            }
        }
        groups.forEach((fields, key) => {
            ordered.push({
                key,
                label: fieldCategories?.[key] ?? key,
                fields,
            })
        })
        return ordered
    }, [fieldDefinitions, fieldCategories])

    const toggleField = (name: string) => {
        setSelected((prev) =>
            prev.includes(name)
                ? prev.filter((field) => field !== name)
                : [...prev, name],
        )
    }

    const handleConfirm = () => {
        if (selected.length === 0) return
        onConfirm(selected)
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Choose data fields for this document</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Select which EZOS fields this template should use. Prefilled
                    when sending; placed on the PDF from the builder Custom tab.
                </Typography>
                {groupedFields.map((group) => (
                    <Box key={group.key} mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            {group.label}
                        </Typography>
                        <FormGroup>
                            {group.fields.map((field) => (
                                <Box key={field.name} mb={1}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedSet.has(
                                                    field.name,
                                                )}
                                                onChange={() =>
                                                    toggleField(field.name)
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={
                                            field.optional
                                                ? `${field.name} (optional)`
                                                : field.name
                                        }
                                    />
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                    >
                                        {field.description}
                                    </Typography>
                                </Box>
                            ))}
                        </FormGroup>
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={isSaving || selected.length === 0}
                >
                    {isSaving ? 'Saving…' : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export function builderFieldsForEnabled<T extends { name: string }>(
    builderFields: T[],
    enabledFields: string[],
): T[] {
    const enabled = new Set(enabledFields)
    return builderFields.filter((field) => enabled.has(field.name))
}
